<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">About Us</h1>
      <p class="page-subtitle">Learn about the team and mission behind AlleleBC database</p>
    </div>

    <div class="content-sections">
      <!-- Mission Section -->
      <section class="about-section">
        <div class="section-header">
          <el-icon class="section-icon"><Target /></el-icon>
          <h2 class="section-title">Our Mission</h2>
        </div>
        <p class="section-text">
          AlleleBC is dedicated to building a world-leading database for allele-specific expression and methylation research.
          We provide high-quality data resources and analytical tools for biomedical researchers, advancing the development
          of precision medicine through comprehensive genomic data integration and analysis.
        </p>
        <div class="mission-stats">
          <div class="stat-card">
            <div class="stat-number">3.7M+</div>
            <div class="stat-label">Genetic Variants</div>
          </div>
          <div class="stat-card">
            <div class="stat-number">50K+</div>
            <div class="stat-label">Annotated Genes</div>
          </div>
          <div class="stat-card">
            <div class="stat-number">2.3M+</div>
            <div class="stat-label">AMR Regions</div>
          </div>
        </div>
      </section>

      <!-- Research Group Section -->
      <section class="about-section">
        <div class="section-header">
          <el-icon class="section-icon"><UserFilled /></el-icon>
          <h2 class="section-title">Research Group</h2>
        </div>
        <div class="institution-info">
          <h3 class="institution-name">Zhu Jiang Bioinformatics Research Group</h3>
          <p class="institution-details">
            <strong>Department:</strong> Medical Informatics<br>
            <strong>Institution:</strong> Harbin Medical University (Daqing Campus)<br>
            <strong>Location:</strong> Daqing, Heilongjiang Province, China
          </p>
          <p class="section-text">
            Our research group focuses on bioinformatics, computational biology, and precision medicine.
            We specialize in developing innovative methods for analyzing complex genomic data,
            with particular expertise in allele-specific expression, epigenetic modifications,
            and their roles in human disease.
          </p>
        </div>

        <div class="team-grid">
          <div class="team-member">
            <div class="member-icon">
              <el-icon><User /></el-icon>
            </div>
            <h3 class="member-name">Principal Investigator</h3>
            <p class="member-role">Bioinformatics Expert</p>
            <p class="member-desc">Leading research in computational genomics and precision medicine</p>
          </div>
          <div class="team-member">
            <div class="member-icon">
              <el-icon><Monitor /></el-icon>
            </div>
            <h3 class="member-name">Technical Lead</h3>
            <p class="member-role">Database Architect</p>
            <p class="member-desc">Designing and maintaining high-performance genomic databases</p>
          </div>
          <div class="team-member">
            <div class="member-icon">
              <el-icon><Cpu /></el-icon>
            </div>
            <h3 class="member-name">Data Scientist</h3>
            <p class="member-role">Computational Biologist</p>
            <p class="member-desc">Developing algorithms for genomic data analysis and interpretation</p>
          </div>
        </div>
      </section>

      <!-- Research Focus Section -->
      <section class="about-section">
        <div class="section-header">
          <el-icon class="section-icon"><DataAnalysis /></el-icon>
          <h2 class="section-title">Research Focus</h2>
        </div>
        <div class="research-areas">
          <div class="research-item">
            <h4>Allele-Specific Expression</h4>
            <p>Investigating differential expression patterns between maternal and paternal alleles and their implications in human disease.</p>
          </div>
          <div class="research-item">
            <h4>Epigenetic Modifications</h4>
            <p>Analyzing DNA methylation patterns and their role in gene regulation and disease susceptibility.</p>
          </div>
          <div class="research-item">
            <h4>Precision Medicine</h4>
            <p>Developing personalized treatment strategies based on individual genetic and epigenetic profiles.</p>
          </div>
          <div class="research-item">
            <h4>Computational Methods</h4>
            <p>Creating innovative bioinformatics tools and algorithms for large-scale genomic data analysis.</p>
          </div>
        </div>
      </section>

      <!-- Collaborations Section -->
      <section class="about-section">
        <div class="section-header">
          <el-icon class="section-icon"><Connection /></el-icon>
          <h2 class="section-title">Collaborations & Partnerships</h2>
        </div>
        <div class="partners">
          <div class="partner-item">
            <el-icon class="partner-icon"><School /></el-icon>
            <div class="partner-info">
              <h4>Academic Institutions</h4>
              <p>Collaborating with leading universities and research institutes worldwide</p>
            </div>
          </div>
          <div class="partner-item">
            <el-icon class="partner-icon"><Hospital /></el-icon>
            <div class="partner-info">
              <h4>Medical Centers</h4>
              <p>Partnering with clinical institutions for translational research</p>
            </div>
          </div>
          <div class="partner-item">
            <el-icon class="partner-icon"><OfficeBuilding /></el-icon>
            <div class="partner-info">
              <h4>Industry Partners</h4>
              <p>Working with biotechnology companies to advance precision medicine</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Contact Section -->
      <section class="about-section contact-section">
        <div class="section-header">
          <el-icon class="section-icon"><Message /></el-icon>
          <h2 class="section-title">Contact Information</h2>
        </div>
        <div class="contact-grid">
          <div class="contact-card">
            <el-icon class="contact-icon"><Message /></el-icon>
            <h4>General Inquiries</h4>
            <p>For general questions about the database and research</p>
            <a href="mailto:contact@foxhank.top" class="contact-link">contact@foxhank.top</a>
          </div>
          <div class="contact-card">
            <el-icon class="contact-icon"><DataAnalysis /></el-icon>
            <h4>Data Collaboration</h4>
            <p>For data submission and research collaborations</p>
            <a href="mailto:contact@foxhank.top" class="contact-link">contact@foxhank.top</a>
          </div>
          <div class="contact-card">
            <el-icon class="contact-icon"><Tools /></el-icon>
            <h4>Technical Support</h4>
            <p>For technical issues and database access problems</p>
            <a href="mailto:contact@foxhank.top" class="contact-link">contact@foxhank.top</a>
          </div>
        </div>

        <div class="address-info">
          <h4>Postal Address</h4>
          <p>
            Zhu Jiang Bioinformatics Research Group<br>
            Department of Medical Informatics<br>
            Harbin Medical University (Daqing Campus)<br>
            Daqing, Heilongjiang Province, China
          </p>
        </div>
      </section>

      <!-- Funding & Acknowledgments -->
      <section class="about-section">
        <div class="section-header">
          <el-icon class="section-icon"><Trophy /></el-icon>
          <h2 class="section-title">Funding & Acknowledgments</h2>
        </div>
        <p class="section-text">
          This research is supported by grants from national and provincial funding agencies.
          We acknowledge the contributions of all researchers, collaborators, and data contributors
          who have made this database possible. Special thanks to the open-source community
          for providing essential tools and resources.
        </p>
        <div class="acknowledgment-note">
          <p>
            <strong>Citation:</strong> When using data from AlleleBC, please cite:
            "AlleleBC Database: A comprehensive resource for allele-specific expression and methylation analysis.
            Zhu Jiang Bioinformatics Research Group, Harbin Medical University. Available at: https://allelebc.org"
          </p>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import {
  Target,
  UserFilled,
  User,
  Monitor,
  Cpu,
  DataAnalysis,
  Connection,
  School,
  Hospital,
  OfficeBuilding,
  Message,
  Tools,
  Trophy
} from '@element-plus/icons-vue'

export default {
  name: 'About',
  components: {
    Target,
    UserFilled,
    User,
    Monitor,
    Cpu,
    DataAnalysis,
    Connection,
    School,
    Hospital,
    OfficeBuilding,
    Message,
    Tools,
    Trophy
  }
}
</script>

<style scoped>
.page-container {
  min-height: calc(100vh - 150px);
  padding: 60px 20px;
}

.page-header {
  text-align: center;
  margin-bottom: 60px;
}

.page-title {
  font-size: 42px;
  background: linear-gradient(135deg, #2c3e50 0%, #3498db 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 15px;
}

.page-subtitle {
  font-size: 18px;
  color: #666;
}

.content-sections {
  max-width: 1000px;
  margin: 0 auto;
}

.about-section {
  background: white;
  border-radius: 20px;
  padding: 40px;
  margin-bottom: 30px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
}

.section-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 25px;
}

.section-icon {
  font-size: 32px;
  color: #3498db;
  background: rgba(52, 152, 219, 0.1);
  padding: 12px;
  border-radius: 12px;
}

.section-title {
  font-size: 28px;
  color: #2c3e50;
  margin: 0;
  font-weight: 600;
}

.section-text {
  font-size: 16px;
  line-height: 1.8;
  color: #555;
  margin-bottom: 25px;
}

.mission-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 20px;
  margin-top: 30px;
}

.stat-card {
  text-align: center;
  padding: 25px;
  background: linear-gradient(135deg, #2c3e50 0%, #3498db 100%);
  color: white;
  border-radius: 15px;
}

.stat-number {
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 8px;
  display: block;
}

.stat-label {
  font-size: 14px;
  opacity: 0.9;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.institution-info {
  margin-bottom: 30px;
}

.institution-name {
  font-size: 24px;
  color: #2c3e50;
  margin: 0 0 15px 0;
  font-weight: 600;
}

.institution-details {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 10px;
  margin-bottom: 20px;
  border-left: 4px solid #3498db;
}

.team-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 25px;
  margin-top: 30px;
}

.team-member {
  text-align: center;
  padding: 30px;
  background: #f8f9fa;
  border-radius: 15px;
  transition: all 0.3s;
  border: 1px solid #e9ecef;
}

.team-member:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  border-color: #3498db;
}

.member-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #2c3e50 0%, #3498db 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 32px;
  color: white;
}

.member-name {
  font-size: 20px;
  margin: 0 0 8px 0;
  color: #2c3e50;
  font-weight: 600;
}

.member-role {
  color: #3498db;
  font-weight: 500;
  margin-bottom: 12px;
  font-size: 16px;
}

.member-desc {
  color: #666;
  font-size: 14px;
  line-height: 1.5;
}

.research-areas {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 25px;
}

.research-item {
  padding: 25px;
  background: #f8f9fa;
  border-radius: 12px;
  border-left: 4px solid #3498db;
}

.research-item h4 {
  margin: 0 0 15px 0;
  color: #2c3e50;
  font-size: 18px;
  font-weight: 600;
}

.research-item p {
  margin: 0;
  color: #555;
  line-height: 1.6;
  font-size: 14px;
}

.partners {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 25px;
}

.partner-item {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 25px;
  background: #f8f9fa;
  border-radius: 12px;
  transition: all 0.3s;
}

.partner-item:hover {
  background: #e9ecef;
  transform: translateX(5px);
}

.partner-icon {
  font-size: 40px;
  color: #3498db;
  background: white;
  padding: 15px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.partner-info h4 {
  margin: 0 0 8px 0;
  color: #2c3e50;
  font-size: 18px;
}

.partner-info p {
  margin: 0;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
}

.contact-section {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.contact-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 25px;
  margin-bottom: 30px;
}

.contact-card {
  background: white;
  padding: 30px;
  border-radius: 15px;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
}

.contact-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

.contact-icon {
  font-size: 48px;
  color: #3498db;
  margin-bottom: 20px;
}

.contact-card h4 {
  margin: 0 0 12px 0;
  color: #2c3e50;
  font-size: 18px;
}

.contact-card p {
  margin: 0 0 15px 0;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
}

.contact-link {
  color: #3498db;
  text-decoration: none;
  font-weight: 500;
  font-size: 16px;
}

.contact-link:hover {
  text-decoration: underline;
}

.address-info {
  background: white;
  padding: 25px;
  border-radius: 12px;
  border-left: 4px solid #3498db;
}

.address-info h4 {
  margin: 0 0 15px 0;
  color: #2c3e50;
  font-size: 18px;
}

.address-info p {
  margin: 0;
  color: #555;
  line-height: 1.8;
}

.acknowledgment-note {
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 10px;
  padding: 25px;
  margin-top: 25px;
}

.acknowledgment-note p {
  margin: 0;
  color: #856404;
  line-height: 1.7;
  font-size: 14px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .page-container {
    padding: 40px 15px;
  }

  .about-section {
    padding: 25px 20px;
  }

  .page-title {
    font-size: 32px;
  }

  .section-header {
    flex-direction: column;
    text-align: center;
    gap: 10px;
  }

  .team-grid,
  .research-areas,
  .partners,
  .contact-grid {
    grid-template-columns: 1fr;
  }

  .partner-item {
    flex-direction: column;
    text-align: center;
  }

  .mission-stats {
    grid-template-columns: 1fr;
  }
}
</style>